import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./Users.css";
import NumberFormat from "react-number-format";
import { FiPlus, FiUserCheck, FiSearch, FiEye } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import swal from "@sweetalert/with-react";
import ReactPaginate from "react-paginate";
import Card from "../../components/Card";
import Loader from "../../components/Loader";
import Modal from "../../components/Modal";
import Table, { Td, Th } from "../../components/Table";
import TextInput from "../../components/TextInput";
import Button from "../../components/Button";
import BigLoader from "../../components/BigLoader";
import { setInterface } from "../../redux/slices/interfaceSlice";
import service from "../../service";

export default function Users() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);

  const [courses, setCourses] = useState([]);
  const [deepReads, setDeepReads] = useState([]);
  const [grantUser, setGrantUser] = useState(null);
  const [grantItems, setGrantItems] = useState([]); // [{key,type,id,title,price,free}]
  const [pickType, setPickType] = useState("course");
  const [pickId, setPickId] = useState("");

  const _modalAdd = useRef();
  const _modalGrant = useRef();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { fullname: "", email: "", username: "", password: "" },
  });

  const getData = () => {
    setLoading(true);
    service
      .get("/api/admin/users", { params: { q: search, page } })
      .then((response) => {
        setData(response.data.data || []);
        setTotal(response.data.total || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    dispatch(setInterface({ active: "Users", pageTitle: "Users" }));
  }, [dispatch]);

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const loadItems = () => {
    service.get("/api/course", { params: { limit: 100 } }).then((r) => {
      setCourses(r.data.data || r.data || []);
    }).catch(() => {});
    service.get("/api/deep-read", { params: { limit: 100 } }).then((r) => {
      setDeepReads(r.data.data || r.data || []);
    }).catch(() => {});
  };

  const addUser = (values) => {
    setLoadingPost(true);
    const payload = { fullname: values.fullname, email: values.email };
    if (values.username) payload.username = values.username;
    if (values.password) payload.password = values.password;
    service
      .post("/api/admin/users", payload)
      .then((res) => {
        setLoadingPost(false);
        _modalAdd.current.hide();
        reset();
        getData();
        swal(
          "User dibuat",
          `Username: ${res.data.username}\nPassword: ${res.data.password}\n\nCatat password ini — hanya ditampilkan sekali.`,
          "success"
        );
      })
      .catch((e) => {
        setLoadingPost(false);
        const msg = e?.response?.data?.errors
          ? Object.values(e.response.data.errors).flat().join(", ")
          : e?.response?.data?.message || "Gagal membuat user";
        swal("Gagal", String(msg), "error");
      });
  };

  const openGrant = (user) => {
    setGrantUser(user);
    setGrantItems([]);
    setPickType("course");
    setPickId("");
    loadItems();
    _modalGrant.current.show();
  };

  const addItemToGrant = () => {
    if (pickType !== "subscription" && !pickId) {
      swal("Pilih produk dulu", "", "warning");
      return;
    }
    const source = pickType === "course" ? courses : deepReads;
    let item;
    if (pickType === "subscription") {
      item = { type: "subscription", id: "sub", title: "Baring Membership", price: 0 };
    } else {
      const found = source.find((x) => String(x.id) === String(pickId));
      if (!found) return;
      item = { type: pickType, id: found.id, title: found.title, price: found.price || 0 };
    }
    const key = `${item.type}-${item.id}`;
    if (grantItems.some((g) => g.key === key)) {
      swal("Produk sudah ditambahkan", "", "info");
      return;
    }
    setGrantItems([...grantItems, { ...item, key, free: pickType === "subscription" }]);
    setPickId("");
  };

  const toggleFree = (key) => {
    setGrantItems(
      grantItems.map((g) => (g.key === key ? { ...g, free: !g.free } : g))
    );
  };

  const removeGrantItem = (key) => {
    setGrantItems(grantItems.filter((g) => g.key !== key));
  };

  const grantTotal = grantItems.reduce((sum, g) => sum + (g.free ? 0 : g.price), 0);

  const submitGrant = () => {
    if (grantItems.length === 0) {
      swal("Tambah minimal satu produk", "", "warning");
      return;
    }
    setLoadingPost(true);
    const payload = {
      items: grantItems.map((g) => ({
        item_type: g.type,
        ...(g.type !== "subscription" ? { item_id: Number(g.id) } : {}),
        free: g.free,
      })),
    };
    service
      .post(`/api/admin/users/${grantUser.id}/grant-access`, payload)
      .then(() => {
        setLoadingPost(false);
        _modalGrant.current.hide();
        swal("Berhasil", `Akses diaktifkan untuk ${grantUser.fullname}`, "success");
      })
      .catch((e) => {
        setLoadingPost(false);
        swal("Gagal", e?.response?.data?.error || "Gagal mengaktifkan akses", "error");
      });
  };


  return (
    <Fragment>
      <Helmet>
        <title>Users</title>
      </Helmet>
      <Card>
        <div className="mb-4 flex flex-wrap gap-3 justify-between items-center">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearch(q);
            }}
            className="flex gap-2"
          >
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama / username / email"
              className="border border-white-300 rounded-sm px-3 py-2 text-sm w-64"
            />
            <Button type="submit" color="blue">
              <FiSearch />
            </Button>
          </form>
          <Button color="red" onClick={() => _modalAdd.current.show()}>
            <FiPlus className="mr-1" /> Tambah User
          </Button>
        </div>

        {loading ? (
          <div className="h-64 flex justify-center items-center flex-col">
            <Loader className="bg-black-100" />
            <span className="mt-3 text-black-300 text-sm">Memuat</span>
          </div>
        ) : data.length ? (
          <Fragment>
            <Table>
              <thead>
                <tr>
                  <Th>NAMA</Th>
                  <Th>USERNAME</Th>
                  <Th>EMAIL</Th>
                  <Th>BERGABUNG</Th>
                  <Th>AKSI</Th>
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.id}>
                    <Td>{u.fullname}</Td>
                    <Td>{u.username}</Td>
                    <Td>{u.email}</Td>
                    <Td className="text-center">
                      {u.created_at ? moment(u.created_at).format("DD/MM/YYYY") : "-"}
                    </Td>
                    <Td>
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => history.push(`/users/${u.id}`)}
                          type="button"
                          title="Lihat detail user"
                          className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 flex items-center"
                        >
                          <FiEye />
                        </button>
                        <button
                          onClick={() => openGrant(u)}
                          type="button"
                          title="Aktifkan akses"
                          className="p-1.5 rounded-sm border border-white-300 text-sm transition-colors hover:bg-green-200 hover:border-green-300 hover:text-white-100 flex items-center"
                        >
                          <FiUserCheck />
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <ReactPaginate
              pageCount={total}
              pageRangeDisplayed={2}
              marginPagesDisplayed={2}
              initialPage={page - 1}
              containerClassName="flex flex-wrap mt-6 gap-1"
              previousLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              nextLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              pageLinkClassName="border border-white-300 rounded-sm py-1.5 px-3 text-sm transition-colors hover:bg-primary-500 hover:border-primary-600 hover:text-white-100 block"
              activeLinkClassName="bg-primary-500 border-primary-600 text-white-100"
              onPageChange={({ selected }) => setPage(selected + 1)}
            />
          </Fragment>
        ) : (
          <div className="text-center flex flex-col justify-center items-center h-40 text-black-300">
            <p>Belum ada user</p>
          </div>
        )}
      </Card>

      <Modal title="Tambah User" ref={_modalAdd}>
        <form onSubmit={handleSubmit(addUser)}>
          <TextInput
            label="Nama Lengkap"
            containerClassName="mb-4"
            message={errors.fullname?.message}
            {...register("fullname", { required: "Tidak boleh kosong" })}
          />
          <TextInput
            label="Email"
            type="email"
            containerClassName="mb-4"
            message={errors.email?.message}
            {...register("email", { required: "Tidak boleh kosong" })}
          />
          <TextInput
            label="Username (opsional — dari email jika kosong)"
            containerClassName="mb-4"
            message={errors.username?.message}
            {...register("username")}
          />
          <TextInput
            label="Password (opsional — auto jika kosong)"
            type="text"
            containerClassName="mb-4"
            message={errors.password?.message}
            {...register("password")}
          />
          <Button type="submit" color="blue" className="w-28">
            Simpan
          </Button>
        </form>
      </Modal>

      <Modal title={`Aktifkan Akses — ${grantUser?.fullname || ""}`} ref={_modalGrant}>
        <div className="mb-3">
          <label className="block text-sm font-semibold text-black-200 mb-2">Tambah Produk</label>
          <div className="flex gap-2 items-center">
            <select
              value={pickType}
              onChange={(e) => {
                setPickType(e.target.value);
                setPickId("");
              }}
              className="ug-select border border-white-300 rounded-sm px-3 py-2 text-sm"
              style={{ flexShrink: 0 }}
            >
              <option value="course">Online Course</option>
              <option value="deep_read">Deep Read</option>
              <option value="subscription">Baring Membership</option>
            </select>
            {pickType !== "subscription" && (
              <select
                value={pickId}
                onChange={(e) => setPickId(e.target.value)}
                className="ug-select ug-select-grow border border-white-300 rounded-sm px-3 py-2 text-sm"
              >
                <option value="">— pilih produk —</option>
                {(pickType === "course" ? courses : deepReads).map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.title}
                  </option>
                ))}
              </select>
            )}
            <Button color="blue" onClick={addItemToGrant} type="button">
              Tambah
            </Button>
          </div>
        </div>

        {grantItems.length > 0 && (
          <div className="mb-4 border border-white-200 rounded-sm">
            {grantItems.map((g) => (
              <div
                key={g.key}
                className="flex items-center justify-between gap-3 px-3 py-2 border-b border-white-200 last:border-b-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-black-200 truncate">{g.title}</div>
                  <div className="text-xs text-black-400">
                    {g.type === "course"
                      ? "Online Course"
                      : g.type === "deep_read"
                      ? "Deep Read"
                      : "Baring Membership"}
                  </div>
                </div>
                <div className="text-sm font-bold text-black-200 whitespace-nowrap">
                  {g.free ? (
                    <span className="text-green-500">FREE</span>
                  ) : (
                    <NumberFormat
                      prefix="Rp"
                      thousandSeparator={true}
                      value={g.price}
                      displayType="text"
                    />
                  )}
                </div>
                {g.type !== "subscription" && (
                  <label className="flex items-center gap-1 text-xs text-black-300 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={g.free}
                      onChange={() => toggleFree(g.key)}
                      className="w-4 h-4"
                    />
                    FREE
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => removeGrantItem(g.key)}
                  className="text-red-400 hover:text-red-500 text-sm px-1"
                  title="Hapus"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mb-4 pt-2 border-t border-white-200">
          <span className="text-sm font-semibold text-black-200">Total Invoice</span>
          <span className="text-base font-bold text-black-100">
            <NumberFormat
              prefix="Rp"
              thousandSeparator={true}
              value={grantTotal}
              displayType="text"
            />
          </span>
        </div>

        <Button color="blue" onClick={submitGrant} className="w-36">
          Aktifkan Akses
        </Button>
      </Modal>

      {loadingPost && <BigLoader />}
    </Fragment>
  );
}
